import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { PRODUCTS_QUERY_KEY } from '@/api/queries/product/key';
import { BULK_UPLOAD_DRAFT_QUERY_KEY } from '@/api/queries/product-bulk/key';
import {
  extractBulkUploadFile,
  fetchBulkUploadDraft,
  submitBulkUpload,
  updateBulkUploadDraft,
  validateBulkUpload,
} from './request';

export const useBulkUploadDraft = (enabled = true) =>
  useQuery({
    queryKey: [BULK_UPLOAD_DRAFT_QUERY_KEY],
    queryFn: async () => {
      try {
        const response = await fetchBulkUploadDraft();
        return response.data;
      } catch {
        return null;
      }
    },
    enabled,
    retry: false,
  });

export const useExtractBulkUploadFile = () =>
  useMutation({
    mutationFn: extractBulkUploadFile,
  });

export const useValidateBulkUpload = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: validateBulkUpload,
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [BULK_UPLOAD_DRAFT_QUERY_KEY],
      });
    },
  });
};

export const useUpdateBulkUploadDraft = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateBulkUploadDraft,
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [BULK_UPLOAD_DRAFT_QUERY_KEY],
      });
    },
  });
};

export const useSubmitBulkUpload = (onSuccess?: () => void) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: submitBulkUpload,
    onSuccess: ({ data }) => {
      if (data.failed === 0) {
        toast.success('Your products have been uploaded successfully.');
      } else if (data.successful > 0) {
        toast.success(
          `${data.successful} products uploaded successfully. ${data.failed} products require corrections.`
        );
      } else {
        toast.error(
          'Upload failed. Please review the error report and try again.'
        );
      }

      queryClient.invalidateQueries({ queryKey: [PRODUCTS_QUERY_KEY] });
      queryClient.invalidateQueries({
        queryKey: [BULK_UPLOAD_DRAFT_QUERY_KEY],
      });
      onSuccess?.();
    },
  });
};
